import Model from '@/models/Model.js';
import Role, { INSTRUCTOR } from '@/models/Role.js';
import { getCachedUserGroupCourseIds, setCachedUserGroupCourseIds } from '@/models/userGroupCourseIdsCache.js';

export default class UserGroup extends Model {
    static entity = 'user_groups';

    static fields() {
        return {
            id: this.attr(null),
            user_id: this.attr(null),
            group_id: this.attr(null),
            role_id: this.attr(null),

            ...super.fields(),

            user: this.belongsTo('users', 'user_id'),
            group: this.belongsTo('groups', 'group_id'),
            role: this.belongsTo('roles', 'role_id'),
        };
    }

    get courseIds() {
        if (this.group?.courses) {
            return this.group.courses.map(course => course.id);
        }

        if (this._courseIds) {
            return this._courseIds;
        }
        return [];
    }

    async fetchCourseIds() {
        const cached = getCachedUserGroupCourseIds(this.id);

        if (Array.isArray(cached)) {
            this._courseIds = cached;

            return cached;
        }

        if (Array.isArray(this._courseIds)) {
            return this._courseIds;
        }

        let lastError;

        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const response = await this.constructor.api().get(`/api/user-groups/${this.id}/course-ids`, {
                    save: false,
                });
                const courseIds = response.response?.data?.data?.course_ids;

                if (!Array.isArray(courseIds)) {
                    return [];
                }

                this._courseIds = courseIds;
                setCachedUserGroupCourseIds(this.id, courseIds);

                return courseIds;
            } catch (error) {
                lastError = error;
            }
        }

        throw lastError;
    }

    hasCourse(courseId) {
        const normalizedCourseId = Number.parseInt(courseId, 10);

        return this.courseIds.some(id => Number.parseInt(id, 10) === normalizedCourseId);
    }

    async hasCourseAsync(courseId) {
        const courseIds = await this.fetchCourseIds();
        const normalizedCourseId = Number.parseInt(courseId, 10);

        return courseIds.some(id => Number.parseInt(id, 10) === normalizedCourseId);
    }

    hasInstructorRole() {
        return this.role_id === INSTRUCTOR;
    }

    hasPermissionTo(permission) {
        return Role.query().with('permissions').find(this.role_id).hasPermissionTo(permission);
    }

    static async getCourseIds(userGroupId) {
        let lastError;

        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const response = await this.api().get(`/api/user-groups/${userGroupId}/course-ids`, { save: false });
                const courseIds = response.response?.data?.data?.course_ids;

                if (!Array.isArray(courseIds)) {
                    return [];
                }

                return courseIds;
            } catch (error) {
                lastError = error;
            }
        }

        console.error(`[UserGroup] Failed to fetch course IDs for user group ${userGroupId}:`, lastError);

        throw lastError;
    }
}
